import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Drawer, Box, Typography, Button, Stack,
  IconButton, Divider, Chip,
} from '@mui/material';
import { DatePicker }           from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment }        from '@mui/x-date-pickers/AdapterMoment';
import moment                   from 'moment';
import FilterListIcon  from '@mui/icons-material/FilterList';
import CloseIcon       from '@mui/icons-material/Close';
import RestartAltIcon  from '@mui/icons-material/RestartAlt';

import { applyFilters, FILTERS_UPDATE } from '../../actions/siteActions';
import { cascade, D_START, D_END }      from './filterCascade';
import { inputSx }                      from './filterStyles';
import MultiDropdown                    from './MultiDropdown';
import SingleDropdown                   from './SingleDropdown';
import FilterField                      from './FilterField';
import SectionLabel                     from './SectionLabel';

const FiltersPanel = () => {
  const dispatch      = useDispatch();
  const combos        = useSelector((s) => s.sites.networkCombos);
  const featureTypes  = useSelector((s) => s.sites.featureTypes);
  const storedFilters = useSelector((s) => s.sites.filters);
  const loading       = useSelector((s) => s.sites.loading);

  const [open, setOpen]       = useState(false);
  const [local, setLocal]     = useState({ ...storedFilters });
  const [dateError, setDateError] = useState('');

  // FIX: re-initialise local state from the current store truth every time the drawer opens.
  // This prevents stale local values when external actions (e.g. MapLegend activityStatus
  // toggle or a Reset from another session) have updated the store while the drawer was closed.
  const handleOpen = () => {
    setLocal({ ...storedFilters });
    setDateError('');
    setOpen(true);
  };

  const handleClose = () => setOpen(false);

  const { availTechs, availVendors, availSides, availServers, availTacs } = useMemo(
    () => cascade(combos, local.technology ? [local.technology] : [], local.vendors, local.sides, local.servers),
    [combos, local.technology, local.vendors, local.sides, local.servers]
  );

  // Type options come from TrackerFeature (Id/Name/Color/SortOrder), fetched once and
  // stored in state.sites.featureTypes — same source used for the map's Type colours.
  const typeOptions = useMemo(() => (featureTypes || []).map((t) => t.name), [featureTypes]);

  const handleTechChange = (field, value) => {
    setLocal((prev) => ({ ...prev, technology: value, vendors: [], sides: [], servers: [], tacs: [] }));
  };

  // Cascade order: Technology -> Vendor -> Side -> Server -> TAC
  const toggle = (field, value) => {
    setLocal((prev) => {
      const cur     = prev[field] || [];
      const next    = cur.includes(value) ? cur.filter((v) => v !== value) : [...cur, value];
      const cleared = {};
      if (field === 'technologies') { cleared.vendors = []; cleared.sides = []; cleared.servers = []; cleared.tacs = []; }
      if (field === 'vendors')      { cleared.sides = []; cleared.servers = []; cleared.tacs = []; }
      if (field === 'sides')        { cleared.servers = []; cleared.tacs = []; }
      if (field === 'servers')      { cleared.tacs = []; }
      return { ...prev, [field]: next, ...cleared };
    });
  };

  const selectAll = (field, options) =>
    setLocal((prev) => ({ ...prev, [field]: prev[field].length === options.length ? [] : [...options] }));

  const handleDateChange = (field) => (momentVal) => {
    const v = momentVal && momentVal.isValid() ? momentVal.format('YYYY-MM-DD') : '';
    setLocal((prev) => {
      const next  = { ...prev, [field]: v };
      const start = field === 'startDate' ? v : prev.startDate;
      const end   = field === 'endDate'   ? v : prev.endDate;
      if (start && end && end <= start) {
        setDateError('End date must be after start date');
      } else {
        setDateError('');
      }
      return next;
    });
    // Instant store update for display-window recolour (no API call)
    dispatch({ type: FILTERS_UPDATE, payload: { [field]: v } });
  };

  const handleApply = () => {
    const next = {
      ...storedFilters,
      ...local,
      technologies: local.technology ? [local.technology] : [],
      features:     local.features   || [],
      windowStart:  local.startDate  || storedFilters.windowStart,
      windowEnd:    local.endDate    || storedFilters.windowEnd,
    };
    dispatch(applyFilters(next));
    handleClose();
  };

  const handleReset = () => {
    const reset = {
      ...storedFilters,
      technology: '4G', technologies: ['4G'],
      vendors: [], servers: [], tacs: [], sides: [], features: [], selectedTitles: [], selectedSites: [],
      startDate: D_START, endDate: D_END,
      windowStart: D_START, windowEnd: D_END,
    };
    setLocal(reset);
    setDateError('');
    dispatch(applyFilters(reset));
    handleClose();
  };

  const activeCount = (local.technology ? 1 : 0)
    + (local.vendors?.length  || 0)
    + (local.servers?.length  || 0)
    + (local.tacs?.length     || 0)
    + (local.sides?.length    || 0);

  return (
    <>
      <Box sx={{ position: 'absolute', top: 12, right: 16, zIndex: 1000 }}>
        <Button variant="contained" startIcon={<FilterListIcon />} onClick={handleOpen}
          sx={{
            backgroundColor: '#0D1B2A', color: '#fff', borderRadius: 2,
            boxShadow: '0 4px 20px rgba(0,0,0,0.3)', textTransform: 'none', fontWeight: 700, px: 2,
            border: '1px solid #1E3A5F',
            '&:hover': { backgroundColor: '#1565C0', borderColor: '#1565C0' },
          }}>
          Filters
          {activeCount > 0 && (
            <Chip label={activeCount} size="small"
              sx={{ ml: 1, backgroundColor: '#FF6B35', color: '#fff', height: 18, fontSize: 10 }} />
          )}
        </Button>
      </Box>

      <Drawer anchor="right" open={open} onClose={handleClose}
        sx={{ zIndex: 1300 }}
        PaperProps={{
          sx: {
            width: 340, top: 56, height: 'calc(100% - 56px)',
            backgroundColor: '#0D1B2A', color: '#E0E7FF',
            display: 'flex', flexDirection: 'column',
          },
        }}>

        {/* Header */}
        <Stack direction="row" alignItems="center" justifyContent="space-between"
          sx={{ px: 2.5, py: 2, borderBottom: '1px solid #1E3A5F', flexShrink: 0 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <FilterListIcon sx={{ color: '#4FC3F7' }} />
            <Typography fontWeight={700} sx={{ color: '#E0E7FF', fontSize: 16 }}>Filters</Typography>
          </Stack>
          <IconButton onClick={handleClose} size="small" sx={{ color: '#90A4AE' }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>

        {/* Body */}
        <Box sx={{ px: 2.5, py: 2.5, flexGrow: 1, overflowY: 'auto' }}>
          <Stack spacing={2}>
            <SectionLabel label="Network" note="Cascade in-memory · Apply reloads map" />

            <SingleDropdown label="Technology" field="technology"
              selected={local.technology || ''} options={availTechs}
              onChange={handleTechChange} />

            <MultiDropdown label="Vendor" field="vendors"
              selected={local.vendors || []} options={availVendors}
              onToggle={toggle} onSelectAll={selectAll} />

            <MultiDropdown label="Side" field="sides"
              selected={local.sides || []} options={availSides}
              onToggle={toggle} onSelectAll={selectAll} />

            <MultiDropdown label="Server (OMC_ID)" field="servers"
              selected={local.servers || []} options={availServers}
              onToggle={toggle} onSelectAll={selectAll} />

            <MultiDropdown label="TAC" field="tacs"
              selected={local.tacs || []} options={availTacs}
              onToggle={toggle} onSelectAll={selectAll} />

            <Divider sx={{ borderColor: '#1E3A5F' }} />

            <SectionLabel label="Type Highlight" note="Highlights sites with selected type" />

            <MultiDropdown label="Type" field="features"
              selected={local.features || []} options={typeOptions}
              onToggle={toggle} onSelectAll={selectAll} />

            <LocalizationProvider dateAdapter={AdapterMoment}>
              <FilterField label="From">
                <DatePicker
                  value={local.startDate ? moment(local.startDate) : null}
                  onChange={handleDateChange('startDate')}
                  maxDate={local.endDate ? moment(local.endDate).subtract(1, 'day') : undefined}
                  format="YYYY-MM-DD"
                  slotProps={{
                    textField: { fullWidth: true, size: 'small', sx: inputSx },
                    openPickerButton: { sx: { color: '#90A4AE' } },
                  }}
                />
              </FilterField>
              <FilterField label="To">
                <DatePicker
                  value={local.endDate ? moment(local.endDate) : null}
                  onChange={handleDateChange('endDate')}
                  minDate={local.startDate ? moment(local.startDate).add(1, 'day') : undefined}
                  format="YYYY-MM-DD"
                  slotProps={{
                    textField: { fullWidth: true, size: 'small', sx: inputSx },
                    openPickerButton: { sx: { color: '#90A4AE' } },
                  }}
                />
              </FilterField>
            </LocalizationProvider>
          </Stack>
        </Box>

        {/* Date error */}
        {dateError && (
          <Box sx={{ px: 2.5, pb: 1 }}>
            <Typography sx={{ color: '#FF6B35', fontSize: 11, fontWeight: 600 }}>{dateError}</Typography>
          </Box>
        )}

        {/* Footer */}
        <Box sx={{ px: 2.5, py: 2, borderTop: '1px solid #1E3A5F', flexShrink: 0 }}>
          <Stack spacing={1.5}>
            <Button fullWidth variant="contained" onClick={handleApply} disabled={loading || !!dateError}
              sx={{ backgroundColor: '#1565C0', textTransform: 'none', fontWeight: 700, borderRadius: 2 }}>
              {loading ? 'Loading…' : 'Apply Filters'}
            </Button>
            <Button fullWidth variant="outlined" startIcon={<RestartAltIcon />} onClick={handleReset}
              sx={{
                borderColor: '#455A64', color: '#90A4AE', textTransform: 'none', borderRadius: 2,
                '&:hover': { borderColor: '#FF6B35', color: '#FF6B35' },
              }}>
              Reset to Defaults
            </Button>
          </Stack>
        </Box>
      </Drawer>
    </>
  );
};

export default FiltersPanel;
